import {
  MedicationsTemplate,
  type MedicationsTemplateRow,
  MedicationsDigitalTemplate,
  type MedicationsDigitalTemplateRow,
} from '../../templates';
import { DocumentSource, DocumentType, MedicationDataType } from '../types';
import type { DocumentEntry, DocumentKey } from '../types';
import type { RawPractitioner, RawSourceMetadata } from './_shared';
import { SourceType } from '@/feature/patients/types';
import { getProviderFacilityPdfEntry } from './pdfOverride';

type MedicationsDataKey = Extract<
  DocumentKey,
  { source: DocumentSource.MEDICAL_DATA; type: DocumentType.MEDICATIONS }
>;

interface RawMedication {
  id: string;
  name: string | null;
  text?: string;
  status?: string;
  startDatetime?: string;
  endDatetime?: string;
  doseUnit?: string;
  doseRoute?: string;
  doseValue?: string;
  patientInstructions?: string;
  practitioner?: RawPractitioner;
  dateUploaded?: string;
  medicationId?: string;
  code?: string;
  codeSystem?: string;
  practitionerRoleId?: string;
  statementId?: string;
  resourceType?: string;
  medicationLabel?: string;
  primaryCode?: string;
  primaryCodingSystem?: string;
  sourceMetadata?: RawSourceMetadata;
  patientId?: string;
  medicationCodes: string[];
  statementText?: string;
  surescriptsMedicationDetails?: {
    prescriberPracticeName?: string;
    prescriberNpi?: string;
    quantityValue?: string;
    quantityUnit?: string;
    daysSupply?: number;
    refillsRemaining?: number;
    substitutions?: number;
    prescriberOrderNumber?: string;
    priorAuthNumber?: string;
    diagnosisQualifier?: string;
    medicationHistorySources: Array<{ qualifier: string; description: string }>;
  };
}

export interface MedicationsDataQueryData {
  medications: RawMedication[];
}

const mapRowToDigitalRow = (
  m: RawMedication,
): MedicationsDigitalTemplateRow => ({
  id: m.id,
  name: m.name,
  dosageValue: m.doseValue,
  dosageUnit: m.doseUnit,
  dosageForm: m.doseRoute,
  diagnosis: m.patientInstructions,
  prescriberFirstName: m.practitioner?.givenName,
  prescriberLastName: m.practitioner?.familyName,
  status: m.status,
  writtenDate: m.startDatetime,
  lastFillDate: m.endDatetime,
  prescriberPracticeName:
    m.surescriptsMedicationDetails?.prescriberPracticeName,
  prescriberNpi: m.surescriptsMedicationDetails?.prescriberNpi,
  quantityValue: m.surescriptsMedicationDetails?.quantityValue,
  quantityUnit: m.surescriptsMedicationDetails?.quantityUnit,
  daysSupply: m.surescriptsMedicationDetails?.daysSupply,
  refillsRemaining: m.surescriptsMedicationDetails?.refillsRemaining,
  prescriberOrderNumber: m.surescriptsMedicationDetails?.prescriberOrderNumber,
  priorAuthNumber: m.surescriptsMedicationDetails?.priorAuthNumber,
  diagnosisQualifier: m.surescriptsMedicationDetails?.diagnosisQualifier,
  sources: m.surescriptsMedicationDetails?.medicationHistorySources,
});

const mapRow = (m: RawMedication): MedicationsTemplateRow => ({
  id: m.id,
  name: m.name,
  text: m.text,
  statementText: m.statementText,
  status: m.status,
  startDatetime: m.startDatetime,
  endDatetime: m.endDatetime,
  doseUnit: m.doseUnit,
  doseValue: m.doseValue,
  doseRoute: m.doseRoute,
  patientInstructions: m.patientInstructions,
  practitioner: m.practitioner,
  dateUploaded: m.dateUploaded,
});

export const adaptMedicationsData = (
  data: MedicationsDataQueryData,
  key: MedicationsDataKey,
): DocumentEntry | undefined => {
  const rawMedications = data?.medications ?? [];
  if (rawMedications.length === 0) return undefined;

  const pdf = getProviderFacilityPdfEntry(key.sourceType, rawMedications, {
    title: key.name,
  });
  if (pdf) return pdf;

  if (key.sourceType === SourceType.MEDICATIONS_DIGITAL_DATA) {
    const digitalMedications = rawMedications.map(mapRowToDigitalRow);
    return {
      kind: 'template',
      title: key.name,
      medicationDataType: MedicationDataType.MedicationsDigitalData,
      highlightAnchorId: key.medicationId
        ? `medication-${key.medicationId}`
        : undefined,
      render: (ctx) => (
        <MedicationsDigitalTemplate
          medications={digitalMedications}
          patient={ctx?.patient}
          generatedAt={new Date()}
          highlightMedicationId={key.medicationId}
        />
      ),
    };
  }

  const medications = rawMedications.map(mapRow);
  return {
    kind: 'template',
    title: key.name,
    medicationDataType: MedicationDataType.MedicationsJson,
    highlightAnchorId: key.medicationId
      ? `medication-${key.medicationId}`
      : undefined,
    render: (ctx) => (
      <MedicationsTemplate
        medications={medications}
        patient={ctx?.patient}
        filename={ctx?.filename}
        generatedAt={new Date()}
        highlightMedicationId={key.medicationId}
      />
    ),
  };
};
